import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Save, Award, GraduationCap, FileText, Camera, Trash2, User } from "lucide-react";

export default function Portfolio() {
  const { user, setUser } = useAuth();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/portfolio/me").then((r) => setP({
      ...r.data,
      research_interests: (r.data.research_interests || []).join(", "),
      affiliations: (r.data.affiliations || []).join(", "),
      awards: (r.data.awards || []).join(", "),
    }));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        headline: p.headline,
        bio: p.bio,
        research_interests: p.research_interests.split(",").map((s) => s.trim()).filter(Boolean),
        affiliations: p.affiliations.split(",").map((s) => s.trim()).filter(Boolean),
        publications: p.publications || [],
        certifications: p.certifications || [],
        awards: p.awards.split(",").map((s) => s.trim()).filter(Boolean),
        orcid: p.orcid,
        google_scholar: p.google_scholar,
      };
      await api.put("/portfolio/me", payload);
      toast.success("Portfolio saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addPublication = () => setP({ ...p, publications: [...(p.publications || []), { title: "", venue: "", year: new Date().getFullYear(), url: "" }] });
  const updatePublication = (i, f, v) => {
    const next = (p.publications || []).map((pub, idx) => (idx === i ? { ...pub, [f]: v } : pub));
    setP({ ...p, publications: next });
  };
  const removePublication = (i) => setP({ ...p, publications: (p.publications || []).filter((_, idx) => idx !== i) });

  const onPickPhoto = () => fileRef.current?.click();

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Image must be under 1.5 MB"); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await api.post("/users/me/picture", { image_base64: dataUrl });
      if (setUser && user) setUser({ ...user, picture: res.data.picture });
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    if (!confirm("Remove profile photo?")) return;
    try {
      await api.delete("/users/me/picture");
      if (setUser && user) setUser({ ...user, picture: null });
      toast.success("Photo removed");
    } catch (err) {
      toast.error("Remove failed");
    }
  };

  const addCert = () => setP({ ...p, certifications: [...(p.certifications || []), { name: "", issuer: "", year: new Date().getFullYear() }] });
  const updateCert = (i, f, v) => {
    const next = (p.certifications || []).map((c, idx) => (idx === i ? { ...c, [f]: v } : c));
    setP({ ...p, certifications: next });
  };
  const removeCert = (i) => setP({ ...p, certifications: (p.certifications || []).filter((_, idx) => idx !== i) });

  if (!p) return <div className="kb-mono text-xs text-kb-ink-muted">Loading portfolio…</div>;

  return (
    <div data-testid="portfolio-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RESEARCHER PORTFOLIO</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
        Your <em className="italic text-kb-primary">academic homepage</em>.
      </h1>

      <form onSubmit={save} className="mt-8 space-y-6">
        <div className="kb-card p-7">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PROFILE</div>

          {/* Avatar */}
          <div className="mt-4 flex items-center gap-4">
            <div className="relative w-20 h-20 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center" data-testid="portfolio-avatar">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-slate-300" strokeWidth={1.5} />
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={onPickPhoto}
                disabled={uploading}
                className="kb-mono text-[11px] uppercase tracking-wider inline-flex items-center gap-1.5 text-kb-primary hover:underline disabled:opacity-50"
                data-testid="portfolio-avatar-upload-btn"
              >
                <Camera className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : (user?.picture ? "Change photo" : "Upload photo")}
              </button>
              {user?.picture && (
                <button type="button" onClick={removePhoto} className="kb-mono text-[11px] uppercase tracking-wider inline-flex items-center gap-1.5 text-rose-500 hover:underline" data-testid="portfolio-avatar-remove-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileChosen} data-testid="portfolio-avatar-input" />
              <span className="text-[10px] text-kb-ink-light">JPG, PNG · max 1.5 MB</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Name</label>
              <input className="kb-input mt-1.5" value={user?.name || ""} disabled />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Email</label>
              <input className="kb-input mt-1.5" value={user?.email || ""} disabled />
            </div>
            <div className="sm:col-span-2">
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Headline</label>
              <input
                className="kb-input mt-1.5"
                placeholder="e.g. NLP researcher focused on low-resource languages"
                value={p.headline || ""}
                onChange={(e) => setP({ ...p, headline: e.target.value })}
                data-testid="portfolio-headline-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Bio</label>
              <textarea
                rows={4}
                className="kb-input mt-1.5 resize-none"
                placeholder="A short bio (2-4 sentences)…"
                value={p.bio || ""}
                onChange={(e) => setP({ ...p, bio: e.target.value })}
                data-testid="portfolio-bio-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Research interests (comma separated)</label>
              <input
                className="kb-input mt-1.5"
                placeholder="NLP, Multilingual ML, Low-resource languages"
                value={p.research_interests || ""}
                onChange={(e) => setP({ ...p, research_interests: e.target.value })}
                data-testid="portfolio-interests-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Affiliations (comma separated)</label>
              <input
                className="kb-input mt-1.5"
                value={p.affiliations || ""}
                onChange={(e) => setP({ ...p, affiliations: e.target.value })}
                data-testid="portfolio-affiliations-input"
              />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">ORCID</label>
              <input
                className="kb-input mt-1.5"
                placeholder="0000-0000-0000-0000"
                value={p.orcid || ""}
                onChange={(e) => setP({ ...p, orcid: e.target.value })}
                data-testid="portfolio-orcid-input"
              />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Google Scholar URL</label>
              <input
                className="kb-input mt-1.5"
                value={p.google_scholar || ""}
                onChange={(e) => setP({ ...p, google_scholar: e.target.value })}
                data-testid="portfolio-scholar-input"
              />
            </div>
          </div>
        </div>

        <div className="kb-card p-7">
          <div className="flex items-center justify-between">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" /> PUBLICATIONS
            </div>
            <button type="button" onClick={addPublication} className="kb-btn-outline text-xs" data-testid="portfolio-add-pub">+ Add publication</button>
          </div>
          <div className="mt-4 space-y-3">
            {(p.publications || []).length === 0 && (
              <div className="text-sm text-kb-ink-muted">No publications added yet.</div>
            )}
            {(p.publications || []).map((pub, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-4 grid sm:grid-cols-12 gap-3">
                <input className="kb-input sm:col-span-6" placeholder="Title" value={pub.title} onChange={(e) => updatePublication(i, "title", e.target.value)} data-testid={`portfolio-pub-${i}-title`} />
                <input className="kb-input sm:col-span-3" placeholder="Venue" value={pub.venue} onChange={(e) => updatePublication(i, "venue", e.target.value)} data-testid={`portfolio-pub-${i}-venue`} />
                <input type="number" className="kb-input sm:col-span-1" placeholder="Year" value={pub.year} onChange={(e) => updatePublication(i, "year", e.target.value)} data-testid={`portfolio-pub-${i}-year`} />
                <input className="kb-input sm:col-span-2" placeholder="URL" value={pub.url} onChange={(e) => updatePublication(i, "url", e.target.value)} data-testid={`portfolio-pub-${i}-url`} />
                <button type="button" onClick={() => removePublication(i)} className="sm:col-span-12 text-xs text-rose-600 text-left" data-testid={`portfolio-pub-${i}-remove`}>Remove</button>
              </div>
            ))}
          </div>
        </div>

        <div className="kb-card p-7">
          <div className="flex items-center justify-between">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2">
              <GraduationCap className="w-3.5 h-3.5" /> CERTIFICATIONS
            </div>
            <button type="button" onClick={addCert} className="kb-btn-outline text-xs" data-testid="portfolio-add-cert">+ Add certification</button>
          </div>
          <div className="mt-4 space-y-3">
            {(p.certifications || []).length === 0 && (
              <div className="text-sm text-kb-ink-muted">No certifications added yet.</div>
            )}
            {(p.certifications || []).map((c, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-4 grid sm:grid-cols-12 gap-3">
                <input className="kb-input sm:col-span-6" placeholder="Name" value={c.name} onChange={(e) => updateCert(i, "name", e.target.value)} data-testid={`portfolio-cert-${i}-name`} />
                <input className="kb-input sm:col-span-4" placeholder="Issuer" value={c.issuer} onChange={(e) => updateCert(i, "issuer", e.target.value)} data-testid={`portfolio-cert-${i}-issuer`} />
                <input type="number" className="kb-input sm:col-span-2" placeholder="Year" value={c.year} onChange={(e) => updateCert(i, "year", e.target.value)} data-testid={`portfolio-cert-${i}-year`} />
                <button type="button" onClick={() => removeCert(i)} className="sm:col-span-12 text-xs text-rose-600 text-left" data-testid={`portfolio-cert-${i}-remove`}>Remove</button>
              </div>
            ))}
          </div>
        </div>

        <div className="kb-card p-7">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2">
            <Award className="w-3.5 h-3.5" /> AWARDS (COMMA SEPARATED)
          </div>
          <input
            className="kb-input mt-4"
            placeholder="Best Paper Award, IEEE Asia Pacific Fellowship 2024…"
            value={p.awards || ""}
            onChange={(e) => setP({ ...p, awards: e.target.value })}
            data-testid="portfolio-awards-input"
          />
        </div>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} className="kb-btn-primary" data-testid="portfolio-save-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save portfolio"}
          </button>
        </div>
      </form>
    </div>
  );
}
